import axios from 'axios'
import Vue from 'vue'
import {
  getProducts,
  createNewProduct,
  editProduct,
  getCustomers,
  customerCreated,
  updateCustomer,
  getOrders,
  getOrder,
  findInvoiceProcess,
  saveNewOrder,
  getQuote,
  newQuote,
  getLeads,
  addLead,
  addLeadActivities,
  getContracts,
  addContract,
  getShippingBooking,
  addShippingBooking,
  getPackings,
  addPacking,
  getRoute,
  addRoute,
  getRouteTemplate,
  addRouteTemplate,
  getDrivers,
  addDriver,
  getVehicles,
  addVehicle,
  findSuppliers,
  newSupplier,
  editSupplier,
  getPurchaseOrders,
  newPurchaseOrder,
  getInventoryReceivingList,
  importProduct,
  getWarehouseOutReceipts,
  productExport,
  getTransferWarehouseList,
  transferWarehouse,
  getProductInventory,
  getStocksBranch,
  getBOMs,
  addBOM,
  getCustomerCompany,
  newCustomerCompany,
  getCustomerGroups,
  newCustomerGroup,
  getEmployee,
  getPartners,
  newPartner,
} from '@/services/api'
import { toJsonOrder } from '../pages/order/service'

const CHATBOT_URL = process.env.VUE_APP_CHATBOT_URL || 'http://localhost:3099'

// ─── Tool executors ────────────────────────────────────────────────────────────
// Each function receives the input from Claude and calls the corresponding api.js function.
// branchId / companyId are read from Vue.prototype at call-time (set by resetData after login).

const toolExecutors = {
  search_products: async (input) => {
    const branchId = Vue.prototype.$branchId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, branchId }
    const result = await getProducts(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((p) => ({
      id: p.id,
      name: p.name,
      code: p.code,
      price: p.price,
      costPrice: p.costPrice,
      isSelling: p.isSelling,
      productType: p.productType,
    }))
    return { total: result.total, list }
  },

  create_product: async (input) => {
    const companyId = Vue.prototype.$companyId
    const data = { ...input, companyId }
    const result = await createNewProduct(data)
    return { success: true, id: result && result.id, name: result && result.name }
  },

  update_product: async (input) => {
    const result = await editProduct(input)
    return { success: true, data: result }
  },

  search_customers: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, 'companies.id': companyId }
    const result = await getCustomers(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((c) => ({
      id: c.id,
      customerName: c.customerName,
      phoneNumber: c.phoneNumber,
      customerCode: c.customerCode,
      email: c.email,
    }))
    return { total: result.total, list }
  },

  create_customer: async (input) => {
    const branchId = Vue.prototype.$branchId
    const data = {
      genderType: 'OTHER',
      ...input,
    }
    const result = await customerCreated(data, branchId)
    const resData = result && result.data && result.data.data
    return { success: true, id: resData && resData.id }
  },

  update_customer: async (input) => {
    const branchId = Vue.prototype.$branchId
    const result = await updateCustomer(input, branchId)
    return { success: true, data: result }
  },

  // ─── Order tools ────────────────────────────────────────────────────────────
  search_orders: async (input) => {
    const branchId = Vue.prototype.$branchId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, branchId }
    const result = await getOrders(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((o) => ({
      id: o.id,
      code: o.code,
      orderStatus: o.orderStatus,
      paymentStatus: o.paymentStatus,
      grandTotal: o.grandTotal,
      invoiceDate: o.invoiceDate,
      customerName: o.customers && o.customers.customerName,
    }))
    return { total: result.totalCount, list }
  },

  get_order_detail: async (input) => {
    const branchId = Vue.prototype.$branchId
    const result = await getOrder(input.id, branchId)
    const o = result && result.data
    if (!o) return { error: 'Không tìm thấy đơn hàng' }
    return {
      id: o.id,
      code: o.code,
      orderStatus: o.orderStatus,
      paymentStatus: o.paymentStatus,
      stockStatus: o.stockStatus,
      grandTotal: o.grandTotal,
      total: o.total,
      totalDiscount: o.totalDiscount,
      invoiceDate: o.invoiceDate,
      note: o.title,
      customerName: o.customers && o.customers.customerName,
      customerPhone: o.customers && o.customers.phoneNumber,
      items: (o.invoiceInitials || []).map((i) => ({
        productName: i.products && i.products.name,
        productCode: i.products && i.products.code,
        quantity: i.quantity,
        price: i.price,
        total: i.total,
      })),
      processSteps: (o.invoiceProcessSteps || []).map((s) => ({
        stepName: s.stepName,
        processDate: s.processDate,
      })),
    }
  },

  get_order_process: async (input) => {
    const branchId = Vue.prototype.$branchId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, branchId }
    const result = await findInvoiceProcess(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((s) => ({
      id: s.id,
      stepName: s.stepName,
      code: s.code,
      orderCount: s.invoices && s.invoices.length,
      orders: (s.invoices || []).slice(0, 5).map((o) => ({
        id: o.id,
        code: o.code,
        customerName: o.customers && o.customers.customerName,
        orderType: o.orderType,
      })),
    }))
    return { total: result.totalCount, list }
  },

  create_order: async (input) => {
    const { items, customerId, note, orderType = 'DEBT' } = input
    const data = toJsonOrder({ 
      orderStatus: 'UNFINISHED',
      stockStatus: 'UNFINISHED',
      orderType,  
      isB2B: orderType === 'B2B',
      title: note || '',
      customerId: customerId || null,
      invoiceInitials: items.map((item) => ({
        productId: item.productId,
        productQuantity: item.quantity,
        price: item.price,
        total: item.quantity * item.price,
      })),
    })
    const result = await saveNewOrder(data, [])
    const o = result && result.data && result.data.data && result.data.data.data
    return { success: true, id: o && o.id, code: o && o.code }
  },

  // ─── Quote tools ─────────────────────────────────────────────────────────────
  search_quotes: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await getQuote(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((q) => ({
      id: q.id,
      code: q.code,
      name: q.name,
      status: q.status,
      total: q.total,
      currency: q.currency,
      createTime: q.createTime,
      customerName: q.customers && q.customers.customerName,
      partnerName: q.partner && q.partner.name,
      productCount: q.quoteProducts && q.quoteProducts.length,
    }))
    return { total: result.totalCount, list }
  },

  create_quote: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { name, customersId, partnerId, description, currency = 'VND', quoteProducts } = input
    const total = quoteProducts.reduce((sum, p) => sum + p.quantity * p.price, 0)
    const data = {
      name,
      customersId: customersId || null,
      partnerId: partnerId || null,
      description: description || '',
      currency,
      exchangeRate: 1,
      status: 'DRAFT',
      total,
      quoteProducts: quoteProducts.map((p) => ({
        productsId: p.productsId,
        quantity: p.quantity,
        price: p.price,
      })),
    }
    const result = await newQuote(data, companyId)
    const id = result && result.data && result.data.id
    return { success: true, id }
  },

  // ─── Lead tools ──────────────────────────────────────────────────────────────
  search_leads: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 20, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await getLeads(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((l) => ({
      id: l.id,
      leadCode: l.leadCode,
      fullName: l.fullName,
      phone: l.phone,
      email: l.email,
      status: l.status,
      source: l.source,
      score: l.score,
      address: l.address,
      description: l.description,
      pic: l.pic && l.pic.fullName,
    }))
    return { total: result.totalCount, list }
  },

  create_leads: async (input) => {
    const companyId = Vue.prototype.$companyId
    const leads = input.leads.map((l) => ({
      gender: 'Nam',
      status: 'Mới',
      source: 'Khác',
      ...l,
    }))
    const result = await addLead(leads, companyId)
    return { success: result && result.statusCode !== 'ERROR', count: leads.length }
  },

  update_lead: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addLead([input], companyId)
    return { success: result && result.statusCode !== 'ERROR' }
  },

  convert_lead_to_customer: async (input) => {
    const companyId = Vue.prototype.$companyId
    const branchId = Vue.prototype.$branchId

    // 1. Lấy thông tin lead
    const leadsResult = await getLeads({ id: input.leadId, companyId }, 1, 0, 'id_desc')
    const lead = leadsResult.list && leadsResult.list[0]
    if (!lead) return { success: false, error: 'Không tìm thấy lead ID ' + input.leadId }

    // 2. Tạo customer từ dữ liệu lead
    const customerInfo = {
      customerName: lead.fullName,
      phoneNumber: lead.phone,
      email: lead.email,
      customerCode: input.customerCode || undefined,
    }
    const cusResult = await customerCreated(customerInfo, branchId)
    const cusData = cusResult && cusResult.data && cusResult.data.data
    const customerId = cusData && cusData.id

    // 3. Cập nhật trạng thái lead → "Đã chuyển đổi"
    await addLead([{ id: lead.id, status: 'Đã chuyển đổi' }], companyId)

    return {
      success: true,
      customerId,
      customerName: lead.fullName,
      message: `Đã tạo khách hàng "${lead.fullName}" từ lead và cập nhật trạng thái lead.`,
    }
  },

  // ─── Contract tools ───────────────────────────────────────────────────────────
  search_contracts: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await getContracts(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((c) => ({
      id: c.id,
      code: c.code,
      name: c.name,
      status: c.status,
      startDate: c.startDate,
      endDate: c.endDate,
      value: c.value,
      customerName: c.customers && c.customers.customerName,
    }))
    return { total: result.totalCount, list }
  },

  create_contract: async (input) => {
    const companyId = Vue.prototype.$companyId
    const data = {
      status: 'draft',
      contractType: 'Hợp đồng thương mại',
      ...input,
    }
    const result = await addContract(data, companyId)
    return { success: true, id: result && result.id, code: result && result.code }
  },

  update_contract: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addContract(input, companyId)
    return { success: true, data: result }
  },

  // ─── Booking tools ────────────────────────────────────────────────────────────
  search_bookings: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await getShippingBooking(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((b) => ({
      id: b.id,
      code: b.code,
      status: b.status,
      shippingDate: b.shippingDate,
      routeName: b.routes && b.routes.name,
      driverName: b.drivers && b.drivers.fullName,
      orderCount: b.invoices && b.invoices.length,
    }))
    return { total: result.totalCount, list }
  },

  create_booking: async (input) => {
    const companyId = Vue.prototype.$companyId
    const data = {
      ...input,
      companyId,
    }
    const result = await addShippingBooking(data, companyId)
    return { success: true, id: result && result.id, code: result && result.code }
  },

  update_booking: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addShippingBooking(input, companyId)
    return { success: true, data: result }
  },

  // ─── Pack tools ───────────────────────────────────────────────────────────────
  search_packs: async (input) => {
    const branchId = Vue.prototype.$branchId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, branchId }
    const result = await getPackings(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((p) => ({
      id: p.id,
      code: p.code,
      name: p.name,
      status: p.status,
      packDate: p.packDate,
      orderCount: p.invoices && p.invoices.length,
    }))
    return { total: result.totalCount, list }
  },

  create_pack: async (input) => {
    const branchId = Vue.prototype.$branchId
    const result = await addPacking(input, branchId)
    return { success: true, id: result && result.id, code: result && result.code }
  },

  update_pack: async (input) => {
    const branchId = Vue.prototype.$branchId
    const result = await addPacking(input, branchId)
    return { success: true, data: result }
  },

  // ─── Route tools ──────────────────────────────────────────────────────────────
  search_routes: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await getRoute(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((r) => ({
      id: r.id,
      name: r.name,
      code: r.code,
      description: r.description,
      customerCount: r.customers && r.customers.length,
    }))
    return { total: result.totalCount, list }
  },

  create_route: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addRoute(input, companyId)
    return { success: true, id: result && result.id, name: result && result.name }
  },

  update_route: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addRoute(input, companyId)
    return { success: true, data: result }
  },

  // ─── Supplier tools ───────────────────────────────────────────────────────────
  search_suppliers: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await findSuppliers(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((s) => ({
      id: s.id,
      supplierCode: s.supplierCode,
      supplierName: s.supplierName,
      phoneNumber: s.phoneNumber,
      email: s.email,
      address: s.address,
    }))
    return { total: result.totalCount, list }
  },

  create_supplier: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await newSupplier({ ...input, companyId })
    return { success: true, id: result && result.data && result.data.id }
  },

  update_supplier: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await editSupplier({ ...input, companyId })
    return { success: true, data: result }
  },

  // ─── Driver tools ─────────────────────────────────────────────────────────────
  search_drivers: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await getDrivers(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((d) => ({
      id: d.id,
      driverCode: d.driverCode,
      driverName: d.driverName,
      phoneNumber: d.phoneNumber,
      address: d.address,
    }))
    return { total: result.totalCount, list }
  },

  create_driver: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addDriver(input, companyId)
    return { success: true, id: result && result.id }
  },

  update_driver: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addDriver(input, companyId)
    return { success: true, data: result }
  },

  // ─── Vehicle tools ────────────────────────────────────────────────────────────
  search_vehicles: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await getVehicles(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((v) => ({
      id: v.id,
      vehicleCode: v.vehicleCode,
      licensePlate: v.licensePlate,
      vehicleType: v.vehicleType,
      capacity: v.capacity,
    }))
    return { total: result.totalCount, list }
  },

  create_vehicle: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addVehicle(input, companyId)
    return { success: true, id: result && result.id }
  },

  update_vehicle: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addVehicle(input, companyId)
    return { success: true, data: result }
  },

  // ─── Route template tools ─────────────────────────────────────────────────────
  search_route_templates: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await getRouteTemplate(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((r) => ({
      id: r.id,
      code: r.code,
      name: r.name,
      startTime: r.startTime,
      endTime: r.endTime,
      driverName: r.driver && r.driver.driverName,
      vehicleCode: r.vehicle && r.vehicle.vehicleCode,
    }))
    return { total: result.totalCount, list }
  },

  create_route_template: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addRouteTemplate(input, companyId)
    return { success: true, id: result && result.id, name: result && result.name }
  },

  update_route_template: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addRouteTemplate(input, companyId)
    return { success: true, data: result }
  },

  // ─── BOM tools ────────────────────────────────────────────────────────────────
  search_boms: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await getBOMs(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((b) => ({
      id: b.id,
      bomCode: b.bomCode,
      bomName: b.bomName,
      bomType: b.bomType,
      bomStatus: b.bomStatus,
    }))
    return { total: result.totalCount, list }
  },

  create_bom: async (input) => {
    const companyId = Vue.prototype.$companyId
    const data = { bomStatus: 'active', bomType: 'group', ...input }
    const result = await addBOM(data, companyId)
    return { success: true, id: result && result.id }
  },

  update_bom: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addBOM(input, companyId)
    return { success: true, data: result }
  },

  // ─── Customer company tools ───────────────────────────────────────────────────
  search_customer_companies: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await getCustomerCompany(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((c) => ({
      id: c.id,
      code: c.code,
      shortName: c.shortName,
      legalName: c.legalName,
      phoneNumber: c.phoneNumber,
      email: c.email,
      taxNumber: c.taxNumber,
    }))
    return { total: result.totalCount, list }
  },

  create_customer_company: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await newCustomerCompany(companyId, input)
    return { success: true, id: result && result.id }
  },

  update_customer_company: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await newCustomerCompany(companyId, input)
    return { success: true, data: result }
  },

  // ─── Customer group tools ─────────────────────────────────────────────────────
  search_customer_groups: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, 'companies.id': companyId }
    const result = await getCustomerGroups(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((g) => ({
      id: g.id,
      groupName: g.groupName,
      discountPercent: g.discountPercent,
      description: g.description,
    }))
    return { total: result.totalCount, list }
  },

  create_customer_group: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await newCustomerGroup(input, companyId)
    return { success: true, id: result && result.data && result.data.data && result.data.data.id }
  },

  // ─── Employee tools (read-only) ───────────────────────────────────────────────
  search_employees: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await getEmployee(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((e) => ({
      id: e.id,
      employeeCode: e.employeeCode,
      fullName: e.fullName,
      phoneNumber: e.phoneNumber,
      email: e.email,
    }))
    return { total: result.totalCount, list }
  },

  // ─── Partner tools ────────────────────────────────────────────────────────────
  search_partners: async (input) => {
    const companyId = Vue.prototype.$companyId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, companyId }
    const result = await getPartners(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((p) => ({
      id: p.id,
      code: p.code,
      name: p.name,
      phoneNumber: p.phoneNumber,
      email: p.email,
      address: p.address,
    }))
    return { total: result.totalCount, list }
  },

  create_partner: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await newPartner(input, companyId)
    return { success: true, id: result && result.id }
  },

  update_partner: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await newPartner(input, companyId)
    return { success: true, data: result }
  },

  // ─── Stock inventory tools ────────────────────────────────────────────────────
  search_inventory: async (input) => {
    const branchId = Vue.prototype.$branchId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, branchId }
    const result = await getProductInventory(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((i) => ({
      id: i.id,
      productName: i.products && i.products.name,
      productCode: i.products && i.products.code,
      stockName: i.stocks && i.stocks.stockName,
      quantity: i.quantity,
      price: i.products && i.products.costPrice,
    }))
    return { total: result.total, list }
  },

  search_stocks: async (input) => {
    const branchId = Vue.prototype.$branchId
    const result = await getStocksBranch(branchId, input || {})
    const list = (result || []).map((s) => ({
      id: s.id,
      stockName: s.stockName,
      stockCode: s.stockCode,
      isDefault: s.isDefault,
    }))
    return { list }
  },

  // ─── Purchase order tools (đơn mua hàng) ─────────────────────────────────────
  search_purchase_orders: async (input) => {
    const branchId = Vue.prototype.$branchId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, branchIds: [branchId] }
    const result = await getPurchaseOrders(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((p) => ({
      id: p.id,
      code: p.code,
      status: p.status,
      totalMoney: p.totalMoney,
      fromDate: p.fromDate,
      supplierName: p.suppliers && p.suppliers.supplierName,
      itemCount: p.details && p.details.length,
    }))
    return { total: result.totalCount, list }
  },

  create_purchase_order: async (input) => {
    const branchId = Vue.prototype.$branchId
    const { supplierId, description, items } = input
    const data = {
      supplierId: supplierId || null,
      description: description || '',
      fromDate: new Date(),
      endDate: new Date(),
      totalQty: items.reduce((s, i) => s + i.quantity, 0),
      quantity: 0,
      taxMoney: 0,
      totalBeforeTax: items.reduce((s, i) => s + i.quantity * i.price, 0),
      percentTax: 0,
      orderCashPaymentReqs: [],
      orderCardPaymentReqs: [],
      detailsReqs: items.map((i) => ({
        prodId: i.productId,
        quantity: i.quantity,
        price: i.price,
        total: i.quantity * i.price,
      })),
    }
    const result = await newPurchaseOrder(data, [], branchId)
    const d = result && result.data && result.data.data
    return { success: true, id: d && d.id, code: d && d.code }
  },

  // ─── Phiếu nhập kho ───────────────────────────────────────────────────────────
  search_stock_receipts: async (input) => {
    const branchId = Vue.prototype.$branchId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, branchIds: [branchId] }
    const result = await getInventoryReceivingList(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((r) => ({
      id: r.id,
      code: r.code,
      totalMoney: r.totalMoney,
      totalQty: r.totalQty,
      createTime: r.createTime,
      supplierName: r.suppliers && r.suppliers.supplierName,
    }))
    return { total: result.totalCount, list }
  },

  create_stock_receipt: async (input) => {
    const branchId = Vue.prototype.$branchId
    const { supplierId, stockId, description, items } = input
    const detailReqs = items.map((i) => ({
      prodId: i.productId,
      stockId: stockId || i.stockId,
      supplierId: supplierId || null,
      quantity: i.quantity,
      price: i.price,
      total: i.quantity * i.price,
    }))
    const data = {
      totalQty: items.reduce((s, i) => s + i.quantity, 0),
      totalMoney: items.reduce((s, i) => s + i.quantity * i.price, 0),
      des: description || '',
      detailReqs,
      purchaseOrderId: input.purchaseOrderId || 0,
      poNoCheck: false,
      externalCostsReqs: [],
      totalCost: 0,
      totalBeforeCost: items.reduce((s, i) => s + i.quantity * i.price, 0),
    }
    const result = await importProduct(branchId, data, [])
    const d = result && result.data && result.data.data
    return { success: true, id: d && d.id, code: d && d.code }
  },

  // ─── Phiếu xuất kho ───────────────────────────────────────────────────────────
  search_stock_exports: async (input) => {
    const branchId = Vue.prototype.$branchId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, branchIds: [branchId] }
    const result = await getWarehouseOutReceipts(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((r) => ({
      id: r.id,
      code: r.code,
      totalMoney: r.totalMoney,
      totalQty: r.totalQty,
      warehouseOutType: r.warehouseOutType,
      createTime: r.createTime,
    }))
    return { total: result.totalCount, list }
  },

  create_stock_export: async (input) => {
    const branchId = Vue.prototype.$branchId
    const { stockId, description, outType = 'NORMAL', items } = input
    const warehouseOutReqs = items.map((i) => ({
      prodId: i.productId,
      stockId: stockId || i.stockId,
      quantity: i.quantity,
      price: i.price || 0,
      total: i.quantity * (i.price || 0),
    }))
    const data = {
      warehouseOutType: outType,
      totalQty: items.reduce((s, i) => s + i.quantity, 0),
      totalMoney: items.reduce((s, i) => s + i.quantity * (i.price || 0), 0),
      des: description || '',
      warehouseOutReqs,
      externalCostsReqs: [],
      totalCost: 0,
      totalBeforeCost: items.reduce((s, i) => s + i.quantity * (i.price || 0), 0),
    }
    const result = await productExport(branchId, data, [])
    const d = result && result.data && result.data.data
    return { success: true, id: d && d.id, code: d && d.code }
  },

  // ─── Phiếu chuyển kho ─────────────────────────────────────────────────────────
  search_stock_transfers: async (input) => {
    const branchId = Vue.prototype.$branchId
    const { limit = 10, ...whereFilters } = input
    const where = { ...whereFilters, branchIds: [branchId] }
    const result = await getTransferWarehouseList(where, limit, 0, 'id_desc')
    const list = (result.list || []).map((t) => ({
      id: t.id,
      code: t.code,
      status: t.status,
      moneyTotal: t.moneyTotal,
      quantityTotal: t.quantityTotal,
      createTime: t.createTime,
      transferBranchName: t.transferBranch && t.transferBranch.name,
      receiverBranchName: t.receiverBranch && t.receiverBranch.name,
    }))
    return { total: result.totalCount, list }
  },

  create_stock_transfer: async (input) => {
    const branchId = Vue.prototype.$branchId
    const companyId = Vue.prototype.$companyId
    const { receiverBranchId, description, isLocal = true, items } = input
    const detail = items.map((i) => ({
      prodId: i.productId,
      quantity: i.quantity,
      price: i.price || 0,
      total: i.quantity * (i.price || 0),
      transferStockId: i.fromStockId || 0,
      receiverStockId: i.toStockId || 0,
    }))
    const data = {
      transferBranchId: branchId,
      receiverBranchId: receiverBranchId || branchId,
      companyId,
      description: description || '',
      status: 'PENDING',
      moneyTotal: items.reduce((s, i) => s + i.quantity * (i.price || 0), 0),
      quantityTotal: items.reduce((s, i) => s + i.quantity, 0),
      totalBeforeCost: items.reduce((s, i) => s + i.quantity * (i.price || 0), 0),
      totalCostOut: 0,
      totalCostIn: 0,
      externalCostsOutReqs: [],
      externalCostsInReqs: [],
      detail,
      detailIn: isLocal ? detail.map((d) => ({ ...d, transferStockId: 0 })) : [],
    }
    const result = await transferWarehouse(data, [])
    const d = result && result.data && result.data.data
    return { success: true, id: d && d.id, code: d && d.code }
  },

  // ─── Lead activity tools ──────────────────────────────────────────────────────
  create_lead_activity: async (input) => {
    const companyId = Vue.prototype.$companyId
    const result = await addLeadActivities([input], companyId)
    return { success: result && result.statusCode !== 'ERROR' }
  },

  // ─── Custom fields ────────────────────────────────────────────────────────────
  get_custom_fields: async (input) => {
    const allFields = Vue.prototype.$customFields || []
    const fields = allFields
      .filter((f) => f.tableName === input.tableName)
      .map((f) => ({
        code: f.code,
        name: f.name,
        type: f.type,
        required: f.required || false,
        options: f.options || undefined,
        groupId: f.groupId || undefined,
      }))
    return { tableName: input.tableName, fields }
  },
}

// ─── Permission map: tool name → required permission code(s) ─────────────────
// Dùng cùng logic với $permission(): prefix match ("ORDER" khớp "ORDER:NEW"),
// hoặc exact match ("ORDER:NEW"). Nhiều code cách nhau dấu phẩy = OR.
// null = không cần kiểm tra (tool đọc-only hoặc an toàn cho mọi người).
const TOOL_PERMISSIONS = {
  // Products
  search_products:           'ADMIN:PRODUCT',
  create_product:            'ADMIN:PRODUCT',
  update_product:            'ADMIN:PRODUCT',
  search_boms:               'ADMIN:PRODUCT',
  create_bom:                'ADMIN:PRODUCT',
  update_bom:                'ADMIN:PRODUCT',

  // Customers
  search_customers:          'ADMIN:CUSTOMER',
  create_customer:           'ADMIN:CUSTOMER',
  update_customer:           'ADMIN:CUSTOMER',
  search_leads:              'ADMIN:CUSTOMER',
  create_leads:              'ADMIN:CUSTOMER',
  update_lead:               'ADMIN:CUSTOMER',
  convert_lead_to_customer:  'ADMIN:CUSTOMER',
  create_lead_activity:      'ADMIN:CUSTOMER',
  search_customer_companies: 'ADMIN:CUSTOMER',
  create_customer_company:   'ADMIN:CUSTOMER',
  update_customer_company:   'ADMIN:CUSTOMER',
  search_customer_groups:    'ADMIN:CUSTOMER',
  create_customer_group:     'ADMIN:CUSTOMER',
  search_route_templates:    'ADMIN:CUSTOMER',
  create_route_template:     'ADMIN:CUSTOMER',
  update_route_template:     'ADMIN:CUSTOMER',

  // Orders
  search_orders:             'ORDER',
  get_order_detail:          'ORDER',
  get_order_process:         'ORDER',
  create_order:              'ORDER:NEW,ORDER:B2B',

  // Quotes & Contracts
  search_quotes:             'ADMIN',
  create_quote:              'ADMIN',
  search_contracts:          'ADMIN',
  create_contract:           'ADMIN',
  update_contract:           'ADMIN',

  // Shipping
  search_bookings:           'SHIPPING',
  create_booking:            'SHIPPING',
  update_booking:            'SHIPPING',
  search_packs:              'SHIPPING',
  create_pack:               'SHIPPING',
  update_pack:               'SHIPPING',
  search_routes:             'SHIPPING',
  create_route:              'SHIPPING',
  update_route:              'SHIPPING',

  // Stock & Inventory
  search_inventory:          'STOCK',
  search_stocks:             null,
  search_purchase_orders:    'STOCK:PURCHASE',
  create_purchase_order:     'STOCK:PURCHASE',
  search_stock_receipts:     'STOCK:IN',
  create_stock_receipt:      'STOCK:IN',
  search_stock_exports:      'STOCK:OUT',
  create_stock_export:       'STOCK:OUT',
  search_stock_transfers:    'STOCK:TRANSFER',
  create_stock_transfer:     'STOCK:TRANSFER',

  // Suppliers & Partners
  search_suppliers:          'ADMIN:SUPPLIER',
  create_supplier:           'ADMIN:SUPPLIER',
  update_supplier:           'ADMIN:SUPPLIER',
  search_partners:           'ADMIN:SUPPLIER',
  create_partner:            'ADMIN:SUPPLIER',
  update_partner:            'ADMIN:SUPPLIER',

  // Employees / Drivers / Vehicles
  search_employees:          'ADMIN:EMPLOYEE',
  search_drivers:            'ADMIN:EMPLOYEE',
  create_driver:             'ADMIN:EMPLOYEE',
  update_driver:             'ADMIN:EMPLOYEE',
  search_vehicles:           'ADMIN:EMPLOYEE',
  create_vehicle:            'ADMIN:EMPLOYEE',
  update_vehicle:            'ADMIN:EMPLOYEE',

  // Không cần kiểm tra (đọc-only, an toàn mọi user)
  get_custom_fields:         null,
  search_features:           null,
  list_modules:              null,
}

// ─── Execute a single tool call from Claude ───────────────────────────────────
async function executeTool(toolUse) {
  const executor = toolExecutors[toolUse.name]
  if (!executor) {
    return {
      type: 'tool_result',
      tool_use_id: toolUse.id,
      is_error: true,
      content: `Tool không hợp lệ: ${toolUse.name}`,
    }
  }

  // Kiểm tra quyền từ localStorage (currentRoles / isSupperAdmin)
  const requiredPermission = TOOL_PERMISSIONS[toolUse.name]
  if (requiredPermission) {
    const isSupperAdmin = localStorage.getItem('isSupperAdmin') === 'true'
    if (!isSupperAdmin && !Vue.prototype.$permission(requiredPermission)) {
      return {
        type: 'tool_result',
        tool_use_id: toolUse.id,
        is_error: true,
        content: `Bạn không có quyền thực hiện thao tác này (yêu cầu quyền: ${requiredPermission}).`,
      }
    }
  }

  try {
    const output = await executor(toolUse.input)
    return {
      type: 'tool_result',
      tool_use_id: toolUse.id,
      content: JSON.stringify(output),
    }
  } catch (err) {
    return {
      type: 'tool_result',
      tool_use_id: toolUse.id,
      is_error: true,
      content: `Lỗi: ${err.message || 'Không xác định'}`,
    }
  }
}

// ─── Main chat function ────────────────────────────────────────────────────────
/**
 * Sends a chat and handles the tool_use loop until Claude returns a final text response.
 *
 * @param {Array} messages - Anthropic-format conversation history
 * @param {Function} onToolCall - called before each tool execution: (toolName, input) => void
 * @returns {Promise<{ messages: Array, reply: string }>}
 */
// Stream text từng từ, gọi onToken cho mỗi chunk (~30ms/từ)
async function streamText(text, onToken) {
  // Tách theo từ nhưng giữ lại khoảng trắng/newline sau mỗi từ
  const chunks = text.split(/(\s+)/)
  for (const chunk of chunks) {
    if (chunk) {
      onToken(chunk)
      await new Promise((r) => setTimeout(r, 28))
    }
  }
}

/**
 * @param {Array} messages
 * @param {Function} onToolCall - (toolName) => void
 * @param {Function} onToken - (text) => void — gọi từng từ khi stream
 */
export async function sendChat(messages, onToolCall, onToken) {
  let currentMessages = [...messages]

  // Loop to handle chained tool calls
  while (true) {
    const token = localStorage.getItem('token') || ''
    const response = await axios.post(`${CHATBOT_URL}/api/chat`, {
      messages: currentMessages,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    })

    const claudeMessage = response.data

    // Append Claude's response to the history
    currentMessages = [
      ...currentMessages,
      { role: 'assistant', content: claudeMessage.content },
    ]

    // Nếu Claude xong, stream text từng từ rồi return
    if (claudeMessage.stop_reason !== 'tool_use') {
      const textBlock = claudeMessage.content.find((b) => b.type === 'text')
      const reply = textBlock ? textBlock.text : ''
      if (onToken && reply) {
        await streamText(reply, onToken)
      }
      return { messages: currentMessages, reply }
    }

    // Claude wants to call tools — execute them all
    const toolUseBlocks = claudeMessage.content.filter((b) => b.type === 'tool_use')

    const toolResults = []
    for (const toolUse of toolUseBlocks) {
      if (onToolCall) onToolCall(toolUse.name, toolUse.input)
      const result = await executeTool(toolUse)
      toolResults.push(result)
    }

    // Append tool results as a user message (required by Anthropic API format)
    currentMessages = [
      ...currentMessages,
      { role: 'user', content: toolResults },
    ]
    // Continue loop — send updated history back to Claude
  }
}
